import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { downloadBlob } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowLeft, Upload, FileSpreadsheet, ShieldAlert, Users, Pill, ClipboardCheck,
  CheckCircle2, AlertOctagon, PlayCircle, FileDown, Trash2, ClipboardList,
  MapPin, Building2, Plus, X, Link2,
} from "lucide-react";

const TABS = [
  { key: "overview",    label: "Overview" },
  { key: "files",       label: "Files" },
  { key: "mapping",     label: "Mapping" },
  { key: "validation",  label: "Validation" },
  { key: "duplicates",  label: "Duplicates" },
  { key: "medication",  label: "Medication" },
  { key: "documents",   label: "Documents" },
  { key: "test",        label: "Test Migration" },
  { key: "uat",         label: "UAT" },
  { key: "approval",    label: "Approval" },
  { key: "production",  label: "Production" },
  { key: "reconciliation", label: "Reconciliation" },
  { key: "reports",     label: "Reports" },
  { key: "audit",       label: "Audit Log" },
];

const ENTITY_LABEL = {
  service_users: "Service Users", staff: "Staff", medications: "Medication",
  care_plans: "Care Plans", risks: "Risk Assessments",
  daily_notes: "Daily Notes", incidents: "Incidents", training: "Training",
};

const STATUS_BADGE = "text-[10px] uppercase tracking-widest border rounded px-2 py-0.5 font-semibold";

function Section({ title, subtitle, children, right }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md mb-4">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">{title}</div>
          {subtitle && <div className="text-sm text-slate-700 mt-0.5">{subtitle}</div>}
        </div>
        {right}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function OverviewTab({ project, refresh }) {
  const steps = [
    { k: "assessment",  label: "Assessment", done: !!project?.org_name },
    { k: "upload",      label: "Upload",     done: (project?.files || []).length > 0 },
    { k: "mapping",     label: "Mapping",    done: (project?.mappings || []).length > 0 },
    { k: "validation",  label: "Validation", done: ["review_required","ready_for_test","test_migration","awaiting_approval","approved","production_migration","reconciliation_required","completed"].includes(project?.status) },
    { k: "test",        label: "Test",       done: ["test_migration","awaiting_approval","approved","production_migration","reconciliation_required","completed"].includes(project?.status) },
    { k: "approval",    label: "Approval",   done: ["approved","production_migration","reconciliation_required","completed"].includes(project?.status) },
    { k: "production",  label: "Production", done: ["reconciliation_required","completed"].includes(project?.status) },
  ];
  const s = project?.stats || {};
  return (
    <>
      <Section title="Migration Project" subtitle={`${project?.org_name || "—"} · Source: ${project?.current_system || "—"} · Ref ${project?.ref}`}>
        <div className="flex items-center gap-2 overflow-x-auto pb-1 mb-4" data-testid="wizard-progress">
          {steps.map((st, i) => (
            <React.Fragment key={st.k}>
              <div className={`text-[11px] px-2.5 py-1 rounded-full border whitespace-nowrap ${st.done ? "bg-emerald-600 text-white border-emerald-700" : "bg-slate-100 text-slate-600 border-slate-300"}`}>
                {i + 1}. {st.label}
              </div>
              {i < steps.length - 1 && <div className="w-4 h-px bg-slate-300" />}
            </React.Fragment>
          ))}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            ["Total", s.total_records || 0, "text-slate-700"],
            ["Valid", s.valid_records || 0, "text-emerald-700"],
            ["Warnings", s.warnings || 0, "text-amber-700"],
            ["Errors", s.errors || 0, "text-red-700"],
          ].map(([label, val, tone]) => (
            <div key={label} className="border border-slate-200 rounded p-3">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
              <div className={`font-heading text-2xl font-semibold tabular-nums ${tone} mt-0.5`}>{val}</div>
            </div>
          ))}
        </div>
      </Section>
    </>
  );
}

function UploadArea({ project, refresh }) {
  const [entity, setEntity] = useState("service_users");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const onFile = async (file) => {
    if (!file) return;
    setBusy(true);
    const t = toast.loading(`Uploading ${file.name}…`);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/migrations/${project.id}/files?entity=${entity}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${file.name} uploaded and parsed`, { id: t });
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed", { id: t });
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <Section title="Step 3 — Upload"
             subtitle="Drop a CSV, XLSX or JSON export. Data lands in the isolated staging area — never in live records."
             right={
               <a href={`/api/migrations/templates/${entity}/xlsx`} target="_blank" rel="noreferrer"
                  className="text-xs inline-flex items-center gap-1.5 h-8 px-2.5 rounded border border-slate-300 hover:bg-slate-50" data-testid="link-template">
                 <FileSpreadsheet className="w-3.5 h-3.5" /> Template
               </a>
             }>
      <div className="flex items-center gap-3 mb-3">
        <label className="text-[11px] font-semibold text-slate-700 uppercase tracking-widest">Entity</label>
        <select value={entity} onChange={(e) => setEntity(e.target.value)} className="h-9 border border-slate-300 rounded-md px-2 text-sm" data-testid="upload-entity">
          {Object.entries(ENTITY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
      <div
        className={`border-2 border-dashed rounded-md p-8 text-center ${busy ? "opacity-60" : "hover:border-emerald-400"} border-slate-300 bg-slate-50`}
        onDragOver={(e) => { e.preventDefault(); }}
        onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files?.[0]); }}
        data-testid="upload-drop"
      >
        <Upload className="w-6 h-6 text-slate-400 mx-auto" />
        <div className="mt-2 text-sm text-slate-600">Drag &amp; drop or click to select</div>
        <div className="text-[11px] text-slate-400 mt-1">Accepted: .csv .xlsx .json .xml · Max 25 MB · No PHI is logged</div>
        <input ref={inputRef} type="file" accept=".csv,.xlsx,.xls,.json,.xml" onChange={(e) => onFile(e.target.files?.[0])} className="mt-3 text-xs" data-testid="upload-input" />
      </div>

      <div className="mt-5">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Uploaded files</div>
        {(project.files || []).length === 0 ? (
          <div className="text-xs text-slate-400" data-testid="upload-empty">No files uploaded yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                <th className="py-1 pr-2">Filename</th>
                <th className="py-1 pr-2">Entity</th>
                <th className="py-1 pr-2 text-right">Rows</th>
                <th className="py-1 pr-2">Uploaded</th>
                <th className="py-1"></th>
              </tr>
            </thead>
            <tbody>
              {project.files.map((f) => (
                <tr key={f.id} className="border-b border-slate-100" data-testid={`file-row-${f.id}`}>
                  <td className="py-2 pr-2 font-medium text-slate-800">{f.filename}</td>
                  <td className="py-2 pr-2">{ENTITY_LABEL[f.entity] || f.entity}</td>
                  <td className="py-2 pr-2 text-right tabular-nums">{f.row_count}</td>
                  <td className="py-2 pr-2 text-xs text-slate-500">{f.uploaded_by_name || f.uploaded_by}</td>
                  <td className="py-2 text-right">
                    <button type="button" onClick={async () => {
                      if (!window.confirm(`Delete ${f.filename}? All staged rows will be removed.`)) return;
                      await api.delete(`/migrations/${project.id}/files/${f.id}`);
                      toast.success("File deleted");
                      refresh();
                    }} className="text-xs text-red-600 hover:underline inline-flex items-center gap-1" data-testid={`file-delete-${f.id}`}>
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Section>
  );
}

function MappingTab({ project, refresh }) {
  const [canonical, setCanonical] = useState({});
  const [busy, setBusy] = useState(false);
  const [saveAs, setSaveAs] = useState({});
  const [editingXform, setEditingXform] = useState(null); // {entity, source}
  const [aiReview, setAiReview] = useState(null); // {entity, result}
  const [aiBusy, setAiBusy] = useState(null);
  useEffect(() => { api.get("/migrations/lookup/canonical-fields").then((r) => setCanonical(r.data.fields || {})); }, []);
  const mappings = project.mappings || [];

  const runAi = async (entity) => {
    setAiBusy(entity);
    const t = toast.loading("Asking AI to review the mapping…");
    try {
      const r = await api.post(`/migrations/${project.id}/mappings/${entity}/ai-review`);
      setAiReview({ entity, result: r.data });
      toast.success(`AI review complete — ${(r.data.suggestions || []).length} suggestion(s)`, { id: t });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI review failed", { id: t });
    } finally { setAiBusy(null); }
  };

  const applyAiSuggestion = async (entity, suggestion) => {
    const m = mappings.find((mm) => mm.entity === entity);
    if (!m) return;
    const newFields = m.fields.map((f) => f.source === suggestion.source ? {
      ...f,
      target: suggestion.suggested_target || f.target,
      transform: suggestion.suggested_transform || f.transform || null,
      status: suggestion.suggested_target ? "confirmed" : f.status,
    } : f);
    await api.post(`/migrations/${project.id}/mappings`, { entity, fields: newFields });
    toast.success(`Applied AI suggestion for ${suggestion.source}`);
    await refresh();
  };

  const updateField = async (entity, source, patch) => {
    const m = mappings.find((mm) => mm.entity === entity);
    if (!m) return;
    const newFields = m.fields.map((f) => f.source === source ? { ...f, ...patch } : f);
    setBusy(true);
    try {
      await api.post(`/migrations/${project.id}/mappings`, { entity, fields: newFields });
      await refresh();
    } finally { setBusy(false); }
  };

  const saveTemplate = async (entity) => {
    const name = saveAs[entity]?.trim();
    if (!name) return toast.error("Give the template a name first");
    const m = mappings.find((mm) => mm.entity === entity);
    if (!m) return;
    await api.post(`/migrations/${project.id}/mappings`, { entity, fields: m.fields, template_name: name });
    toast.success("Mapping template saved");
    setSaveAs((s) => ({ ...s, [entity]: "" }));
  };

  const xformLabel = (t) => {
    if (!t || !t.kind) return null;
    if (t.kind === "value_map") return `map · ${Object.keys(t.map || {}).length} entries`;
    if (t.kind === "case") return `case · ${t.mode || "?"}`;
    return t.kind;
  };

  if (mappings.length === 0) {
    return <Section title="Step 4 — Field Mapping"><div className="text-sm text-slate-500">Upload a file first — mapping suggestions will appear here.</div></Section>;
  }

  return (
    <>
      {mappings.map((m) => (
        <Section
          key={m.entity + m.file_id} title={`Mapping · ${ENTITY_LABEL[m.entity] || m.entity}`}
          subtitle="Left column shows source fields · confirm the AYOBELL target, add a transform if needed, and save."
          right={
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="h-8" onClick={() => runAi(m.entity)} disabled={aiBusy === m.entity} data-testid={`ai-review-${m.entity}`}>{aiBusy === m.entity ? "Reviewing…" : "AI Review"}</Button>
              <input placeholder="Template name (optional)" value={saveAs[m.entity] || ""} onChange={(e) => setSaveAs((s) => ({ ...s, [m.entity]: e.target.value }))} className="h-8 px-2 border border-slate-300 rounded text-xs" data-testid={`tpl-name-${m.entity}`} />
              <Button variant="outline" onClick={() => saveTemplate(m.entity)} size="sm" className="h-8" data-testid={`tpl-save-${m.entity}`}>Save as template</Button>
            </div>
          }
        >
          <table className="w-full text-sm" data-testid={`mapping-table-${m.entity}`}>
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                <th className="py-1 pr-2">Source field</th>
                <th className="py-1 pr-2">Confidence</th>
                <th className="py-1 pr-2">AYOBELL target</th>
                <th className="py-1 pr-2">Transform</th>
                <th className="py-1">Status</th>
              </tr>
            </thead>
            <tbody>
              {(m.fields || []).map((f) => (
                <tr key={f.source} className="border-b border-slate-100" data-testid={`mapping-row-${m.entity}-${f.source}`}>
                  <td className="py-2 pr-2 font-mono text-[12px]">{f.source}</td>
                  <td className="py-2 pr-2">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${f.confidence >= 90 ? "bg-emerald-50 border-emerald-300 text-emerald-800" : f.confidence >= 55 ? "bg-amber-50 border-amber-300 text-amber-800" : "bg-slate-100 border-slate-300 text-slate-500"}`}>
                      {f.confidence || 0}%
                    </span>
                  </td>
                  <td className="py-2 pr-2">
                    <select value={f.target || ""} onChange={(e) => updateField(m.entity, f.source, { target: e.target.value || null, status: e.target.value ? "confirmed" : "unmapped" })} className="h-8 border border-slate-300 rounded px-1 text-xs w-full max-w-[240px]" disabled={busy}>
                      <option value="">— unmapped —</option>
                      {(canonical[m.entity] || []).map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </td>
                  <td className="py-2 pr-2">
                    <button type="button" onClick={() => setEditingXform({ entity: m.entity, source: f.source, current: f.transform || null })} className="text-[11px] text-sky-700 hover:underline" data-testid={`xform-open-${m.entity}-${f.source}`}>
                      {xformLabel(f.transform) || "+ add transform"}
                    </button>
                  </td>
                  <td className="py-2">
                    <span className={`${STATUS_BADGE} ${f.status === "confirmed" ? "border-emerald-300 bg-emerald-50 text-emerald-800" : f.status === "ignored" ? "border-slate-300 bg-slate-100 text-slate-500" : "border-slate-300 bg-slate-50 text-slate-600"}`}>{f.status || "unmapped"}</span>
                    <button type="button" className="ml-2 text-[10px] text-slate-500 hover:text-slate-800" onClick={() => updateField(m.entity, f.source, { status: f.status === "ignored" ? "unmapped" : "ignored", target: f.status === "ignored" ? f.target : null })} data-testid={`mapping-ignore-${m.entity}-${f.source}`}>
                      {f.status === "ignored" ? "Include" : "Ignore"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      ))}
      {editingXform && (
        <TransformDialog
          entity={editingXform.entity}
          source={editingXform.source}
          current={editingXform.current}
          onClose={() => setEditingXform(null)}
          onSave={(transform) => {
            updateField(editingXform.entity, editingXform.source, { transform });
            setEditingXform(null);
          }}
        />
      )}
      {aiReview && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-start justify-center p-6 overflow-y-auto" data-testid="ai-review-dialog">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mt-10">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">AI Mapping Review · {aiReview.entity}</div>
                <div className="font-heading text-lg font-semibold text-slate-900">Overall confidence: <span className="text-emerald-700">{aiReview.result?.overall_confidence ?? 0}%</span></div>
              </div>
              <button type="button" onClick={() => setAiReview(null)} className="text-slate-400 hover:text-slate-700 text-xl" data-testid="ai-review-close">×</button>
            </div>
            <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
              {(aiReview.result?.safety_notes || []).length > 0 && (
                <div className="bg-amber-50 border-l-4 border-amber-400 rounded p-3 text-[12px] text-amber-900" data-testid="ai-safety-notes">
                  <b>Safety notes.</b>
                  <ul className="list-disc list-inside mt-1">
                    {aiReview.result.safety_notes.map((n, i) => <li key={i}>{n}</li>)}
                  </ul>
                </div>
              )}
              {(aiReview.result?.unmapped_targets || []).length > 0 && (
                <div className="text-[12px] text-slate-600">
                  <b>Important fields still unmapped:</b> {aiReview.result.unmapped_targets.join(", ")}
                </div>
              )}
              <div className="space-y-2">
                {(aiReview.result?.suggestions || []).map((s, i) => (
                  <div key={i} className="border border-slate-200 rounded p-3" data-testid={`ai-suggestion-${i}`}>
                    <div className="flex items-center justify-between">
                      <div className="font-mono text-[12px] text-slate-800">{s.source} → <span className="text-emerald-700">{s.suggested_target || "—"}</span></div>
                      <span className={`${STATUS_BADGE} ${s.risk === "high" ? "border-red-300 bg-red-50 text-red-800" : s.risk === "medium" ? "border-amber-300 bg-amber-50 text-amber-800" : "border-emerald-300 bg-emerald-50 text-emerald-800"}`}>{s.risk || "low"} risk{s.ambiguous ? " · ambiguous" : ""}</span>
                    </div>
                    {s.suggested_transform && (
                      <div className="text-[11px] text-slate-500 mt-1">Transform: <span className="font-mono">{JSON.stringify(s.suggested_transform)}</span></div>
                    )}
                    <div className="text-[12px] text-slate-700 mt-1">{s.reason}</div>
                    {(s.suggested_target || s.suggested_transform) && !s.ambiguous && s.risk !== "high" && (
                      <button type="button" onClick={() => applyAiSuggestion(aiReview.entity, s)} className="mt-2 text-[11px] text-emerald-700 hover:underline" data-testid={`ai-apply-${i}`}>Apply suggestion</button>
                    )}
                  </div>
                ))}
              </div>
              <div className="text-[10px] text-slate-400">AI never approves or activates data. Every suggestion must be accepted by a human.</div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function TransformDialog({ entity, source, current, onClose, onSave }) {
  const [kind, setKind] = useState(current?.kind || "");
  const [caseMode, setCaseMode] = useState(current?.mode || "title");
  const [mapText, setMapText] = useState(
    current?.kind === "value_map"
      ? Object.entries(current.map || {}).map(([k, v]) => `${k}=${v}`).join("\n")
      : ""
  );
  const save = () => {
    if (!kind) return onSave(null);
    if (kind === "case") return onSave({ kind: "case", mode: caseMode });
    if (kind === "value_map") {
      const map = {};
      mapText.split("\n").forEach((ln) => {
        const idx = ln.indexOf("=");
        if (idx > 0) map[ln.slice(0, idx).trim()] = ln.slice(idx + 1).trim();
      });
      return onSave({ kind: "value_map", map });
    }
    onSave({ kind });
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-start justify-center p-6" data-testid="xform-dialog">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mt-16">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Transform</div>
            <div className="font-heading text-lg font-semibold text-slate-900">{source} <span className="text-slate-400 text-sm">({entity})</span></div>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700 text-xl">×</button>
        </div>
        <div className="p-5 space-y-3">
          <label className="text-[11px] font-semibold text-slate-700 uppercase tracking-widest">Kind</label>
          <select value={kind} onChange={(e) => setKind(e.target.value)} className="w-full h-9 border border-slate-300 rounded px-2 text-sm" data-testid="xform-kind">
            <option value="">— none —</option>
            <option value="phone">Normalise phone number</option>
            <option value="gender_enum">Normalise gender (M/F → Male/Female)</option>
            <option value="bool_enum">Normalise boolean (1/Y/Yes → true)</option>
            <option value="date_iso">Parse dates (any → YYYY-MM-DD)</option>
            <option value="case">Change case</option>
            <option value="value_map">Custom value map</option>
          </select>
          {kind === "case" && (
            <div>
              <label className="text-[11px] font-semibold text-slate-700 uppercase tracking-widest">Mode</label>
              <select value={caseMode} onChange={(e) => setCaseMode(e.target.value)} className="w-full h-9 border border-slate-300 rounded px-2 text-sm">
                <option value="upper">UPPERCASE</option>
                <option value="lower">lowercase</option>
                <option value="title">Title Case</option>
              </select>
            </div>
          )}
          {kind === "value_map" && (
            <div>
              <label className="text-[11px] font-semibold text-slate-700 uppercase tracking-widest">Map (one pair per line: <span className="font-mono text-[10px]">source=target</span>)</label>
              <textarea value={mapText} onChange={(e) => setMapText(e.target.value)} rows={6} className="w-full font-mono text-xs border border-slate-300 rounded p-2" placeholder="1=active&#10;0=inactive&#10;M=Male&#10;F=Female" data-testid="xform-map-text" />
            </div>
          )}
          <div className="text-[11px] text-slate-500 pt-1">Transforms run at validation time. Original values are always preserved in the record&apos;s provenance block.</div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="xform-cancel">Cancel</Button>
          <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700" data-testid="xform-save">Save transform</Button>
        </div>
      </div>
    </div>
  );
}

function OldMappingTabPlaceholder(){return null;}

function ValidationTab({ project, refresh }) {
  const [running, setRunning] = useState(false);
  const [issues, setIssues] = useState([]);
  const [severity, setSeverity] = useState("");
  const runValidation = async () => {
    setRunning(true);
    const t = toast.loading("Running validation…");
    try {
      const r = await api.post(`/migrations/${project.id}/validate`);
      toast.success(`Validation complete — ${r.data.totals.valid} valid, ${r.data.totals.warnings} warnings, ${r.data.totals.errors} errors`, { id: t });
      await loadIssues();
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Validation failed", { id: t });
    } finally { setRunning(false); }
  };
  const loadIssues = useCallback(async () => {
    const r = await api.get(`/migrations/${project.id}/issues`, { params: { severity: severity || undefined } });
    setIssues(r.data.items || []);
  }, [project.id, severity]);
  useEffect(() => { loadIssues(); }, [loadIssues]);

  const resolveIssue = async (id, action, reason) => {
    await api.post(`/migrations/${project.id}/issues/${id}/resolve`, { action, reason });
    await loadIssues();
    await refresh();
  };

  return (
    <>
    <UnmatchedSitesPanel project={project} onResolved={async () => { await loadIssues(); await refresh(); }} />
    <Section title="Step 5 — Validation" subtitle="Run the validation engine — issues are classified as blocking, warning or info."
             right={<Button onClick={runValidation} disabled={running} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-run-validation"><PlayCircle className="w-4 h-4 mr-1.5" />{running ? "Running…" : "Run validation"}</Button>}>
      <div className="flex items-center gap-2 mb-3">
        {["", "blocking", "warning", "info"].map((s) => (
          <button key={s || "all"} type="button" onClick={() => setSeverity(s)}
            className={`text-[11px] px-2 py-1 rounded-full border ${severity === s ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300 text-slate-600"}`}
            data-testid={`issues-filter-${s || "all"}`}
          >{s ? s.toUpperCase() : "All"}</button>
        ))}
      </div>
      {issues.length === 0 ? (
        <div className="text-sm text-slate-500" data-testid="issues-empty">No validation issues.</div>
      ) : (
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200"><th className="py-1 pr-2">Entity</th><th className="py-1 pr-2">Row</th><th className="py-1 pr-2">Field</th><th className="py-1 pr-2">Issue</th><th className="py-1 pr-2">Severity</th><th className="py-1"></th></tr></thead>
          <tbody>
            {issues.map((i) => (
              <tr key={i.id} className="border-b border-slate-100 align-top" data-testid={`issue-row-${i.id}`}>
                <td className="py-2 pr-2">{ENTITY_LABEL[i.entity] || i.entity}</td>
                <td className="py-2 pr-2 tabular-nums">{i.row_number || "—"}</td>
                <td className="py-2 pr-2 font-mono text-[12px]">{i.field}</td>
                <td className="py-2 pr-2">{i.message}<div className="text-[10px] text-slate-400 mt-0.5">Original: <span className="font-mono">{i.original_value ?? "—"}</span></div></td>
                <td className="py-2 pr-2"><span className={`${STATUS_BADGE} ${i.severity === "blocking" ? "border-red-300 bg-red-50 text-red-800" : i.severity === "warning" ? "border-amber-300 bg-amber-50 text-amber-800" : "border-sky-300 bg-sky-50 text-sky-800"}`}>{i.severity}</span></td>
                <td className="py-2 text-right whitespace-nowrap">
                  <button type="button" onClick={() => resolveIssue(i.id, "resolve")} className="text-[11px] text-emerald-700 hover:underline mr-2" data-testid={`issue-resolve-${i.id}`}>Mark resolved</button>
                  <button type="button" onClick={() => resolveIssue(i.id, "ignore", "Reviewed and accepted")} className="text-[11px] text-slate-500 hover:text-slate-800" data-testid={`issue-ignore-${i.id}`}>Ignore</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Section>
    </>
  );
}

function UnmatchedSitesPanel({ project, onResolved }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyKey, setBusyKey] = useState(null);
  const [pickerOpenFor, setPickerOpenFor] = useState(null); // source_name
  const [createOpenFor, setCreateOpenFor] = useState(null); // source_name
  const [existingSites, setExistingSites] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/migrations/${project.id}/unmatched-sites`);
      setGroups(r.data.items || []);
    } finally { setLoading(false); }
  }, [project.id]);

  useEffect(() => { load(); }, [load]);

  const loadSites = useCallback(async () => {
    try {
      const r = await api.get(`/sites`);
      setExistingSites(r.data || []);
    } catch { setExistingSites([]); }
  }, []);

  const resolve = async (source_name, payload) => {
    setBusyKey(source_name);
    const t = toast.loading("Resolving site…");
    try {
      const r = await api.post(
        `/migrations/${project.id}/unmatched-sites/resolve`,
        { source_name, ...payload });
      const { action, resolved_site_name, affected_records, totals } = r.data;
      toast.success(
        action === "exclude"
          ? `Excluded ${affected_records} record(s) referencing '${source_name}'`
          : `${action === "create" ? "Created & mapped" : "Mapped"} ${affected_records} record(s) → ${resolved_site_name}. Revalidated: ${totals.errors} errors, ${totals.warnings} warnings.`,
        { id: t, duration: 5000 });
      setPickerOpenFor(null);
      setCreateOpenFor(null);
      await load();
      onResolved && await onResolved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resolve failed", { id: t });
    } finally { setBusyKey(null); }
  };

  if (loading && !groups.length) {
    return (
      <Section title="Unmatched Sites" subtitle="Loading…"><div className="text-sm text-slate-500" data-testid="unmatched-sites-loading">Checking staged records for unresolved site references…</div></Section>
    );
  }
  if (!groups.length) return null;

  const totalAffected = groups.reduce((n, g) => n + g.record_count, 0);

  return (
    <Section
      title="Unmatched Sites"
      subtitle={`${groups.length} legacy site${groups.length === 1 ? "" : "s"} could not be resolved — affects ${totalAffected} staged record${totalAffected === 1 ? "" : "s"}. Site assignment is BLOCKING for staff.`}
      right={<div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid="unmatched-sites-count">{groups.length} unresolved</div>}
    >
      <div className="space-y-3" data-testid="unmatched-sites-list">
        {groups.map((g) => {
          const key = g.source_name;
          const top = g.suggestions && g.suggestions[0];
          const busy = busyKey === key;
          return (
            <div key={key} className="border border-slate-200 rounded-md p-3" data-testid={`unmatched-site-${key}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-amber-600 shrink-0" />
                    <div className="font-heading text-[15px] font-semibold text-slate-900 truncate" title={g.source_name}>{g.source_name}</div>
                    <span className="text-[10px] uppercase tracking-widest border border-red-300 bg-red-50 text-red-800 rounded px-1.5 py-0.5 font-semibold">BLOCKING</span>
                  </div>
                  <div className="text-[12px] text-slate-500 mt-0.5">
                    <span className="tabular-nums font-semibold text-slate-700">{g.record_count}</span> affected record{g.record_count === 1 ? "" : "s"}
                    {g.sample_records && g.sample_records.length > 0 && (
                      <span className="ml-2 text-slate-400">
                        e.g. {g.sample_records.slice(0, 2).map((s) => `${s.preview?.first_name || ""} ${s.preview?.last_name || ""}`.trim() || `row ${s.row_number}`).join(", ")}
                      </span>
                    )}
                  </div>
                  {g.suggestions && g.suggestions.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`suggestions-${key}`}>
                      {g.suggestions.map((s) => (
                        <button
                          key={s.id}
                          type="button"
                          disabled={busy}
                          onClick={() => resolve(key, { action: "map", target_site_id: s.id, reason: `Fuzzy-match ${s.confidence}%` })}
                          className="inline-flex items-center gap-1 text-[11px] border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 rounded-full px-2 py-0.5"
                          title={`Map ${g.record_count} record(s) to ${s.name}`}
                          data-testid={`suggest-${key}-${s.id}`}
                        >
                          <Link2 className="w-3 h-3" />
                          {s.name}
                          <span className="tabular-nums text-emerald-600">·{s.confidence}%</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-1.5 shrink-0">
                  <button type="button" disabled={busy}
                    onClick={() => { loadSites(); setPickerOpenFor(key); }}
                    className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md border border-slate-300 bg-white text-slate-700 text-[12px] hover:bg-slate-50"
                    data-testid={`btn-map-${key}`}>
                    <Building2 className="w-3.5 h-3.5" /> Map to Existing
                  </button>
                  <button type="button" disabled={busy}
                    onClick={() => setCreateOpenFor(key)}
                    className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md border border-emerald-300 bg-emerald-50 text-emerald-800 text-[12px] hover:bg-emerald-100"
                    data-testid={`btn-create-${key}`}>
                    <Plus className="w-3.5 h-3.5" /> Create New Site
                  </button>
                  <button type="button" disabled={busy}
                    onClick={() => {
                      if (window.confirm(`Exclude ${g.record_count} staff record(s) referencing '${g.source_name}'? They will not be promoted.`)) {
                        resolve(key, { action: "exclude", reason: "Excluded via Unmatched Sites panel" });
                      }
                    }}
                    className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md border border-slate-300 bg-white text-slate-500 text-[12px] hover:bg-slate-50 hover:text-red-700"
                    data-testid={`btn-exclude-${key}`}>
                    <X className="w-3.5 h-3.5" /> Exclude
                  </button>
                  {top && (
                    <button type="button" disabled={busy}
                      onClick={() => resolve(key, { action: "map", target_site_id: top.id, reason: `Accepted top suggestion (${top.confidence}%)` })}
                      className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md border border-emerald-600 bg-emerald-600 text-white text-[12px] hover:bg-emerald-700 disabled:opacity-50"
                      data-testid={`btn-accept-top-${key}`}>
                      <CheckCircle2 className="w-3.5 h-3.5" /> Accept top ({top.confidence}%)
                    </button>
                  )}
                </div>
              </div>

              {pickerOpenFor === key && (
                <div className="mt-3 border-t border-slate-100 pt-3" data-testid={`picker-${key}`}>
                  <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2">Pick an Ayobell site</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-48 overflow-auto">
                    {existingSites.map((s) => (
                      <button key={s.id} type="button"
                        onClick={() => resolve(key, { action: "map", target_site_id: s.id, reason: "Manual map via picker" })}
                        className="text-left border border-slate-200 hover:border-emerald-400 hover:bg-emerald-50 rounded px-2 py-1.5"
                        data-testid={`picker-item-${key}-${s.id}`}>
                        <div className="text-[13px] text-slate-800">{s.name}</div>
                        <div className="text-[10px] text-slate-500 truncate">{s.address || "—"}</div>
                      </button>
                    ))}
                    {existingSites.length === 0 && <div className="text-[12px] text-slate-500">No sites exist yet. Use “Create New Site”.</div>}
                  </div>
                  <button type="button" onClick={() => setPickerOpenFor(null)} className="mt-2 text-[11px] text-slate-500 hover:text-slate-800">Close</button>
                </div>
              )}

              {createOpenFor === key && (
                <CreateSiteForm
                  defaultName={g.source_name}
                  onCancel={() => setCreateOpenFor(null)}
                  onSubmit={(payload) => resolve(key, { action: "create", new_site: payload, reason: "Created via Unmatched Sites panel" })}
                  busy={busy}
                />
              )}
            </div>
          );
        })}
      </div>
    </Section>
  );
}

function CreateSiteForm({ defaultName, onCancel, onSubmit, busy }) {
  const [name, setName] = useState(defaultName || "");
  const [address, setAddress] = useState("");
  const [manager, setManager] = useState("");
  const [phone, setPhone] = useState("");
  const submit = (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSubmit({ name: name.trim(), address: address.trim(), manager_name: manager.trim(), phone: phone.trim() });
  };
  return (
    <form onSubmit={submit} className="mt-3 border-t border-slate-100 pt-3 grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="create-site-form">
      <label className="text-[11px] text-slate-600">Site name
        <input value={name} onChange={(e) => setName(e.target.value)} required
          className="mt-1 w-full h-8 px-2 border border-slate-300 rounded text-sm" data-testid="new-site-name" />
      </label>
      <label className="text-[11px] text-slate-600">Address
        <input value={address} onChange={(e) => setAddress(e.target.value)}
          className="mt-1 w-full h-8 px-2 border border-slate-300 rounded text-sm" data-testid="new-site-address" />
      </label>
      <label className="text-[11px] text-slate-600">Manager name
        <input value={manager} onChange={(e) => setManager(e.target.value)}
          className="mt-1 w-full h-8 px-2 border border-slate-300 rounded text-sm" data-testid="new-site-manager" />
      </label>
      <label className="text-[11px] text-slate-600">Phone
        <input value={phone} onChange={(e) => setPhone(e.target.value)}
          className="mt-1 w-full h-8 px-2 border border-slate-300 rounded text-sm" data-testid="new-site-phone" />
      </label>
      <div className="col-span-full flex items-center gap-2 justify-end">
        <button type="button" onClick={onCancel} className="text-[12px] text-slate-500 hover:text-slate-800">Cancel</button>
        <Button type="submit" disabled={busy || !name.trim()} className="bg-emerald-600 hover:bg-emerald-700 h-8 text-[12px]" data-testid="new-site-submit">
          <Plus className="w-3.5 h-3.5 mr-1" /> Create & map records
        </Button>
      </div>
    </form>
  );
}

function DuplicatesTab({ project }) {
  const [items, setItems] = useState([]);
  const load = useCallback(async () => {
    const r = await api.get(`/migrations/${project.id}/duplicates`);
    setItems(r.data.items || []);
  }, [project.id]);
  useEffect(() => { load(); }, [load]);
  const act = async (id, action) => {
    await api.post(`/migrations/${project.id}/duplicates/${id}/resolve`, { action });
    await load();
  };
  return (
    <Section title="Duplicate Detection" subtitle="Potential duplicates against staged rows and existing AYOBELL records.">
      {items.length === 0 ? <div className="text-sm text-slate-500" data-testid="dupes-empty">No duplicates detected.</div> : (
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200"><th className="py-1 pr-2">Kind</th><th className="py-1 pr-2">Match</th><th className="py-1 pr-2">Status</th><th className="py-1"></th></tr></thead>
          <tbody>
            {items.map((d) => (
              <tr key={d.id} className="border-b border-slate-100" data-testid={`dupe-row-${d.id}`}>
                <td className="py-2 pr-2">{d.kind === "vs_existing" ? "vs existing" : "in-batch"}</td>
                <td className="py-2 pr-2">{d.match_summary?.full_name || "—"} · {d.match_summary?.dob || "—"}</td>
                <td className="py-2 pr-2 capitalize">{d.status}</td>
                <td className="py-2 text-right whitespace-nowrap">
                  <button type="button" onClick={() => act(d.id, "merge")} className="text-[11px] text-emerald-700 hover:underline mr-2" data-testid={`dupe-merge-${d.id}`}>Merge</button>
                  <button type="button" onClick={() => act(d.id, "keep_separate")} className="text-[11px] text-slate-600 hover:underline mr-2" data-testid={`dupe-keep-${d.id}`}>Keep separate</button>
                  <button type="button" onClick={() => act(d.id, "skip")} className="text-[11px] text-red-600 hover:underline" data-testid={`dupe-skip-${d.id}`}>Skip import</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Section>
  );
}

function DocumentsTab({ project }) {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const load = useCallback(async () => {
    const r = await api.get(`/migrations/${project.id}/documents`);
    setItems(r.data.items || []);
    setCounts(r.data.counts || {});
  }, [project.id]);
  useEffect(() => { load(); }, [load]);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const t = toast.loading(`Uploading ${file.name}…`);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/migrations/${project.id}/documents/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${r.data.count} document(s) staged`, { id: t });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed", { id: t });
    } finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };
  const accept = async (id) => { await api.post(`/migrations/${project.id}/documents/${id}/accept-suggestion`); await load(); };
  const reject = async (id) => { await api.post(`/migrations/${project.id}/documents/${id}/reject`); await load(); };
  return (
    <Section title="Documents & Attachments" subtitle="ZIP or single-file upload. Filenames are fuzzy-matched to service users or staff; anything below the confidence threshold lands in the Unmatched queue for manual assignment.">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
        {[["unmatched","Unmatched","text-red-700"],["suggested","Suggested","text-amber-700"],["assigned","Assigned","text-emerald-700"],["rejected","Rejected","text-slate-500"]].map(([k,label,tone]) => (
          <div key={k} className="border border-slate-200 rounded p-2">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
            <div className={`font-heading text-xl font-semibold tabular-nums ${tone}`}>{counts[k] || 0}</div>
          </div>
        ))}
      </div>
      <div className={`border-2 border-dashed rounded-md p-6 text-center mb-4 ${busy ? "opacity-60" : "hover:border-emerald-400"} border-slate-300 bg-slate-50`}
           onDragOver={(e) => e.preventDefault()}
           onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files?.[0]); }}
           data-testid="doc-drop">
        <Upload className="w-5 h-5 text-slate-400 mx-auto" />
        <div className="mt-2 text-sm text-slate-600">Drop a <b>.zip</b> bundle or a single file</div>
        <div className="text-[11px] text-slate-400">Accepted inside ZIP: pdf, docx, jpg, png, tif, txt · Max 100 MB bundle, 25 MB per doc</div>
        <input ref={inputRef} type="file" accept=".zip,.pdf,.docx,.doc,.png,.jpg,.jpeg,.webp,.tif,.tiff,.heic,.txt,.rtf" onChange={(e) => upload(e.target.files?.[0])} className="mt-2 text-xs" data-testid="doc-upload-input" />
      </div>
      {items.length === 0 ? <div className="text-sm text-slate-500" data-testid="doc-empty">No documents staged yet.</div> : (
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200"><th className="py-1 pr-2">Filename</th><th className="py-1 pr-2">Match Suggestion</th><th className="py-1 pr-2">Status</th><th className="py-1"></th></tr></thead>
          <tbody>
            {items.map((d) => (
              <tr key={d.id} className="border-b border-slate-100 align-top" data-testid={`doc-row-${d.id}`}>
                <td className="py-2 pr-2 font-medium">{d.filename}<div className="text-[10px] text-slate-400 font-mono">{Math.round((d.size_bytes || 0) / 1024)} KB</div></td>
                <td className="py-2 pr-2 text-xs">
                  {d.match_suggestion ? (
                    <div>
                      <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {d.match_suggestion.name} <span className="text-slate-400">({d.match_suggestion.kind})</span></span>
                      <div className="text-[10px] text-slate-500">Confidence {Math.round((d.match_suggestion.score || 0) * 100)}%</div>
                    </div>
                  ) : <span className="text-red-600">No suggestion</span>}
                </td>
                <td className="py-2 pr-2"><span className={`${STATUS_BADGE} ${d.status === "assigned" ? "border-emerald-300 bg-emerald-50 text-emerald-800" : d.status === "rejected" ? "border-slate-300 bg-slate-100 text-slate-500" : d.status === "suggested" ? "border-amber-300 bg-amber-50 text-amber-800" : "border-red-300 bg-red-50 text-red-800"}`}>{d.status}</span></td>
                <td className="py-2 text-right whitespace-nowrap">
                  {d.status !== "assigned" && d.match_suggestion && <button type="button" onClick={() => accept(d.id)} className="text-[11px] text-emerald-700 hover:underline mr-2" data-testid={`doc-accept-${d.id}`}>Accept suggestion</button>}
                  {d.status !== "rejected" && <button type="button" onClick={() => reject(d.id)} className="text-[11px] text-red-700 hover:underline" data-testid={`doc-reject-${d.id}`}>Reject</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Section>
  );
}

function MedicationTab({ project }) {
  const [items, setItems] = useState([]);
  const load = useCallback(async () => {
    const r = await api.get(`/migrations/${project.id}/medications`);
    setItems(r.data.items || []);
  }, [project.id]);
  useEffect(() => { load(); }, [load]);
  const verify = async (id, action) => {
    await api.post(`/migrations/${project.id}/medications/${id}/verify`, { action, reason: action === "reject" ? "Rejected on migration review" : null });
    await load();
  };
  return (
    <Section title="Medication Safety Gate" subtitle="Imported medication is not active until an authorised user verifies each record.">
      <div className="bg-amber-50 border-l-4 border-amber-400 text-amber-900 text-[12px] p-3 rounded mb-4">
        <b>Safety notice.</b> Imported medication cannot become an active eMAR instruction automatically. Each row must be reviewed and either verified, edited &amp; verified, or rejected.
      </div>
      {items.length === 0 ? <div className="text-sm text-slate-500" data-testid="meds-empty">No medication records staged yet.</div> : (
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200"><th className="py-1 pr-2">Service User</th><th className="py-1 pr-2">Medicine</th><th className="py-1 pr-2">Dose</th><th className="py-1 pr-2">Frequency</th><th className="py-1 pr-2">Status</th><th className="py-1"></th></tr></thead>
          <tbody>
            {items.map((v) => {
              const m = v.mapped || {};
              return (
                <tr key={v.id} className="border-b border-slate-100" data-testid={`med-row-${v.id}`}>
                  <td className="py-2 pr-2">{m.client_name || "—"}</td>
                  <td className="py-2 pr-2 font-medium">{m.medicine_name || "—"}</td>
                  <td className="py-2 pr-2">{m.dose || "—"}</td>
                  <td className="py-2 pr-2">{m.frequency || "—"}</td>
                  <td className="py-2 pr-2"><span className={`${STATUS_BADGE} ${v.status === "verified" ? "border-emerald-300 bg-emerald-50 text-emerald-800" : v.status === "rejected" ? "border-red-300 bg-red-50 text-red-800" : "border-amber-300 bg-amber-50 text-amber-800"}`}>{v.status === "pending" ? "Verification Required" : v.status}</span></td>
                  <td className="py-2 text-right whitespace-nowrap">
                    {v.status === "pending" && <>
                      <button type="button" onClick={() => verify(v.id, "activate")} className="text-[11px] text-emerald-700 hover:underline mr-2" data-testid={`med-verify-${v.id}`}>Verify &amp; Activate</button>
                      <button type="button" onClick={() => verify(v.id, "reject")} className="text-[11px] text-red-700 hover:underline" data-testid={`med-reject-${v.id}`}>Reject</button>
                    </>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </Section>
  );
}

function TestTab({ project, refresh }) {
  const [preview, setPreview] = useState([]);
  const [sample, setSample] = useState(5);
  const start = async () => {
    const r = await api.post(`/migrations/${project.id}/test-migration?sample_size=${sample}`);
    toast.success(`Test migration prepared — ${r.data.sampled_service_users} service users`);
    await load();
    await refresh();
  };
  const load = useCallback(async () => {
    const r = await api.get(`/migrations/${project.id}/test-preview`);
    setPreview(r.data.items || []);
  }, [project.id]);
  useEffect(() => { load(); }, [load]);
  return (
    <Section title="Step 6 — Test Migration" subtitle="Sample a handful of records for customer review before production. Data remains isolated from live eMAR / care records."
             right={<div className="flex items-center gap-2"><input type="number" min="1" max="20" value={sample} onChange={(e) => setSample(Number(e.target.value))} className="w-16 h-9 border border-slate-300 rounded px-2 text-sm" data-testid="test-sample-size" /><Button onClick={start} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-test-migration">Prepare test sample</Button></div>}>
      {preview.length === 0 ? <div className="text-sm text-slate-500" data-testid="test-empty">No sample yet — click &quot;Prepare test sample&quot;.</div> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {preview.map((r) => (
            <div key={r.id} className="border border-slate-200 rounded p-3" data-testid={`test-preview-${r.id}`}>
              <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">{ENTITY_LABEL[r.entity] || r.entity}</div>
              <div className="font-medium text-slate-800 mt-0.5">{(r.mapped || {}).full_name || (r.mapped || {}).medicine_name || (r.mapped || {}).title || "—"}</div>
              <div className="text-[11px] text-slate-500 mt-1">Status: {r.status}</div>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

const UAT_LABELS = {
  demographics_verified: "Service user demographics verified",
  care_plans_verified: "Care plans verified",
  risks_verified: "Risk assessments verified",
  medication_verified: "Medication verified",
  allergies_verified: "Allergies verified",
  important_people_verified: "Important people verified",
  daily_notes_checked: "Daily notes history checked",
  incidents_checked: "Incidents checked",
  documents_accessible: "Documents accessible",
  staff_checked: "Staff records checked",
  sites_correct: "Site assignments correct",
  permissions_checked: "Permissions checked",
  reports_checked: "Reports checked",
};

function UatTab({ project, refresh }) {
  const [items, setItems] = useState([]);
  const [state, setState] = useState({});
  const load = useCallback(async () => {
    const r = await api.get(`/migrations/${project.id}/uat`);
    setItems(r.data.items || []);
    setState((r.data.saved && r.data.saved.items) || {});
  }, [project.id]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const r = await api.post(`/migrations/${project.id}/uat`, { items: state });
    if (r.data.all_ok) toast.success("UAT complete — migration is awaiting approval");
    else toast.success("UAT saved");
    refresh();
  };

  return (
    <Section title="Step 7 — UAT Checklist" subtitle="Electronic sign-off of customer-side verification."
             right={<Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-save-uat">Save checklist</Button>}>
      <div className="space-y-2">
        {items.map((k) => (
          <label key={k} className="flex items-start gap-3 border border-slate-200 rounded p-3 hover:bg-slate-50" data-testid={`uat-item-${k}`}>
            <input type="checkbox" className="mt-1"
                   checked={!!state[k]?.ok}
                   onChange={(e) => setState((s) => ({ ...s, [k]: { ...(s[k] || {}), ok: e.target.checked } }))} />
            <div className="flex-1">
              <div className="text-sm font-medium text-slate-800">{UAT_LABELS[k] || k}</div>
              <input type="text" placeholder="Comments"
                     value={state[k]?.comment || ""}
                     onChange={(e) => setState((s) => ({ ...s, [k]: { ...(s[k] || {}), comment: e.target.value } }))}
                     className="mt-1 w-full h-8 border border-slate-200 rounded px-2 text-xs" />
            </div>
          </label>
        ))}
      </div>
    </Section>
  );
}

function ApprovalTab({ project, refresh }) {
  const [confirms, setConfirms] = useState({ reviewed_test: false, checked_critical: false, understands_live: false, reviewed_warnings: false });
  const [comments, setComments] = useState("");
  const { user } = useAuth();
  const submit = async () => {
    try {
      await api.post(`/migrations/${project.id}/approve`, { confirms, comments });
      toast.success("Approved — ready for production migration");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approval failed");
    }
  };
  const items = [
    ["reviewed_test", "I confirm the test migration has been reviewed."],
    ["checked_critical", "I confirm critical data has been checked."],
    ["understands_live", "I understand the production migration will create live AYOBELL records."],
    ["reviewed_warnings", "I confirm outstanding warnings have been reviewed."],
  ];
  return (
    <Section title="Step 8 — Approval" subtitle="Sign-off with your logged-in identity. Approver is set to the current user and cannot be typed as somebody else.">
      <div className="bg-slate-50 border border-slate-200 rounded p-3 mb-3 text-sm">
        <b>Approver:</b> {user?.name || user?.email} · <span className="text-slate-500 capitalize">{user?.role}</span>
      </div>
      <div className="space-y-2 mb-3">
        {items.map(([k, label]) => (
          <label key={k} className="flex items-start gap-3 border border-slate-200 rounded p-3" data-testid={`approve-${k}`}>
            <input type="checkbox" checked={!!confirms[k]} onChange={(e) => setConfirms((c) => ({ ...c, [k]: e.target.checked }))} />
            <span className="text-sm text-slate-800">{label}</span>
          </label>
        ))}
      </div>
      <textarea value={comments} onChange={(e) => setComments(e.target.value)} placeholder="Optional comments" className="w-full min-h-[70px] border border-slate-300 rounded p-2 text-sm mb-3" data-testid="approve-comments" />
      <Button onClick={submit} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-approve">APPROVE FOR PRODUCTION MIGRATION</Button>
    </Section>
  );
}

function ProductionTab({ project, refresh }) {
  const [jobs, setJobs] = useState([]);
  const [rollbackReason, setRollbackReason] = useState("");
  const [showRollback, setShowRollback] = useState(false);
  const load = useCallback(async () => {
    const r = await api.get(`/migrations/${project.id}/jobs`);
    setJobs(r.data || []);
  }, [project.id]);
  useEffect(() => { load(); const t = setInterval(load, 4000); return () => clearInterval(t); }, [load]);
  const start = async () => {
    if (!window.confirm("Start the production migration? A backup marker will be recorded and validated staged records will be promoted into live AYOBELL records.")) return;
    try {
      await api.post(`/migrations/${project.id}/go-live`);
      toast.success("Production migration started");
      await refresh();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start migration");
    }
  };
  const rollback = async () => {
    if (!rollbackReason.trim()) return toast.error("Rollback reason is required.");
    try {
      const r = await api.post(`/migrations/${project.id}/rollback`, { reason: rollbackReason, confirm: true });
      const total = Object.values(r.data.per_entity || {}).reduce((a, b) => a + b, 0);
      toast.success(`Soft-rollback complete — ${total} record(s) flagged`);
      setShowRollback(false);
      setRollbackReason("");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rollback failed");
    }
  };
  const running = jobs.find((j) => j.status === "running");
  const canRollback = ["completed", "reconciliation_required", "production_migration"].includes(project.status);
  return (
    <Section title="Step 8 — Production Migration"
             subtitle="Approved records only. A pre-migration backup marker is recorded. Idempotent: rows already promoted are not re-inserted."
             right={
               <div className="flex items-center gap-2">
                 {canRollback && <Button variant="outline" onClick={() => setShowRollback(true)} className="text-red-700 border-red-300 hover:bg-red-50" data-testid="btn-open-rollback"><AlertOctagon className="w-4 h-4 mr-1.5" /> Rollback</Button>}
                 <Button onClick={start} disabled={project.status !== "approved" || !!running} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-go-live"><PlayCircle className="w-4 h-4 mr-1.5" /> Start Production Migration</Button>
               </div>
             }>
      {jobs.length === 0 ? <div className="text-sm text-slate-500">No production runs yet.</div> : (
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200"><th className="py-1 pr-2">Started</th><th className="py-1 pr-2">By</th><th className="py-1 pr-2">Backup Ref</th><th className="py-1 pr-2">Status</th><th className="py-1">Progress</th></tr></thead>
          <tbody>
            {jobs.map((j) => (
              <tr key={j.id} className="border-b border-slate-100 align-top">
                <td className="py-2 pr-2 text-xs">{new Date(j.started_at).toLocaleString()}</td>
                <td className="py-2 pr-2 text-xs">{j.started_by}</td>
                <td className="py-2 pr-2 font-mono text-[11px]">{j.backup_reference}</td>
                <td className="py-2 pr-2 capitalize">{j.status}</td>
                <td className="py-2 text-xs text-slate-600">
                  {Object.entries(j.progress || {}).map(([e, n]) => <span key={e} className="inline-block mr-3">{ENTITY_LABEL[e] || e}: <b>{n}</b></span>)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {showRollback && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-start justify-center p-6" data-testid="rollback-dialog">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mt-16">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-red-700 font-semibold">Soft-Rollback</div>
                <div className="font-heading text-lg font-semibold text-slate-900">Rollback this migration</div>
              </div>
              <button type="button" onClick={() => setShowRollback(false)} className="text-slate-400 hover:text-slate-700 text-xl">×</button>
            </div>
            <div className="p-5 space-y-3">
              <div className="bg-red-50 border-l-4 border-red-500 rounded p-3 text-[12px] text-red-900">
                <b>Safety.</b> Rollback flags every promoted record with <span className="font-mono">migration.rolled_back = true</span>. Clinical data is NOT deleted — the audit trail and provenance are preserved.
              </div>
              <label className="text-[11px] font-semibold text-slate-700 uppercase tracking-widest">Reason (required)</label>
              <textarea value={rollbackReason} onChange={(e) => setRollbackReason(e.target.value)} rows={3} className="w-full border border-slate-300 rounded p-2 text-sm" placeholder="e.g. Data mismatch found after go-live — pausing for review" data-testid="rollback-reason" />
            </div>
            <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
              <Button variant="outline" onClick={() => setShowRollback(false)}>Cancel</Button>
              <Button onClick={rollback} className="bg-red-600 hover:bg-red-700 text-white" data-testid="btn-confirm-rollback">Confirm rollback</Button>
            </div>
          </div>
        </div>
      )}
    </Section>
  );
}

function ReconTab({ project, refresh }) {
  const [result, setResult] = useState(null);
  const run = async () => {
    const r = await api.post(`/migrations/${project.id}/reconcile`);
    setResult(r.data);
    if (r.data.all_ok) toast.success("Reconciliation OK — migration marked completed");
    else toast.warning("Reconciliation mismatch — see details");
    refresh();
  };
  return (
    <Section title="Reconciliation" subtitle="Source vs migrated counts per entity."
             right={<Button onClick={run} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-reconcile">Run reconciliation</Button>}>
      {!result ? <div className="text-sm text-slate-500" data-testid="recon-empty">Reconcile after the production migration finishes.</div> : (
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200"><th className="py-1 pr-2">Entity</th><th className="py-1 pr-2 text-right">Source</th><th className="py-1 pr-2 text-right">Migrated</th><th className="py-1 pr-2 text-right">Gap</th><th className="py-1">OK</th></tr></thead>
          <tbody>
            {result.per_entity.map((r) => (
              <tr key={r.entity} className="border-b border-slate-100" data-testid={`recon-row-${r.entity}`}>
                <td className="py-2 pr-2">{ENTITY_LABEL[r.entity] || r.entity}</td>
                <td className="py-2 pr-2 text-right tabular-nums">{r.source}</td>
                <td className="py-2 pr-2 text-right tabular-nums">{r.migrated}</td>
                <td className={`py-2 pr-2 text-right tabular-nums ${r.gap === 0 ? "text-emerald-700" : "text-red-700"}`}>{r.gap}</td>
                <td className="py-2">{r.ok ? "✓" : "⚠"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Section>
  );
}

function ReportsTab({ project }) {
  const done = project?.status === "completed";
  return (
    <Section title="Reports" subtitle="Download the migration completion report, certificate and audit-friendly summaries.">
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={async () => {
          const t = toast.loading("Building report…");
          try {
            await downloadBlob(`/migrations/${project.id}/report/pdf`, `migration-${project.ref}.pdf`);
            toast.success("Report downloaded", { id: t });
          } catch { toast.error("Export failed", { id: t }); }
        }} data-testid="btn-download-report"><FileDown className="w-4 h-4 mr-1.5" /> Migration Completion Report (PDF)</Button>
        <Button variant="outline" disabled={!done} onClick={async () => {
          const t = toast.loading("Building certificate…");
          try {
            await downloadBlob(`/migrations/${project.id}/certificate/pdf`, `migration-cert-${project.ref}.pdf`);
            toast.success("Certificate downloaded", { id: t });
          } catch { toast.error("Export failed", { id: t }); }
        }} data-testid="btn-download-certificate">
          <FileDown className="w-4 h-4 mr-1.5" /> Data Migration Completion Certificate (PDF)
        </Button>
      </div>
      {!done && <div className="text-[11px] text-slate-500 mt-2">Certificate is only issued once reconciliation passes and the migration is marked Completed.</div>}
    </Section>
  );
}

function AuditTab({ project }) {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get(`/migrations/${project.id}/audit`).then((r) => setItems(r.data.items || [])); }, [project.id]);
  return (
    <Section title="Audit Log" subtitle="Every significant action recorded during this migration.">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200"><th className="py-1 pr-2">When</th><th className="py-1 pr-2">By</th><th className="py-1 pr-2">Action</th><th className="py-1">Detail</th></tr></thead>
        <tbody>
          {items.length === 0 && <tr><td colSpan={4} className="py-4 text-slate-400 text-center">No audit entries yet.</td></tr>}
          {items.map((a) => (
            <tr key={a.id} className="border-b border-slate-100 align-top">
              <td className="py-2 pr-2 text-xs whitespace-nowrap">{new Date(a.at).toLocaleString()}</td>
              <td className="py-2 pr-2 text-xs">{a.by_name || a.by_email}</td>
              <td className="py-2 pr-2 font-mono text-[11px]">{a.action}</td>
              <td className="py-2 text-xs text-slate-500">{a.new_value ? <span className="font-mono">{JSON.stringify(a.new_value).slice(0, 140)}</span> : (a.record_type || "—")}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Section>
  );
}

export default function MigrationProject() {
  const { id } = useParams();
  const nav = useNavigate();
  const [project, setProject] = useState(null);
  const [tab, setTab] = useState("overview");

  const refresh = useCallback(async () => {
    const r = await api.get(`/migrations/${id}`);
    setProject(r.data);
  }, [id]);
  useEffect(() => { refresh(); }, [refresh]);
  if (!project) return <div className="p-8 text-sm text-slate-400">Loading migration…</div>;

  return (
    <div className="p-6 max-w-[1400px] mx-auto" data-testid="page-migration-project">
      <div className="flex items-center gap-3 mb-4">
        <button type="button" onClick={() => nav("/migration")} className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 text-sm" data-testid="btn-back-migration">
          <ArrowLeft className="w-4 h-4" /> Migrations
        </button>
      </div>
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Migration {project.ref}</div>
          <h1 className="font-heading text-3xl font-semibold text-slate-900 mt-1">{project.org_name}</h1>
          <div className="text-sm text-slate-500 mt-1">Source: {project.current_system || "—"} · Go-live: {project.go_live_date || "TBC"}</div>
        </div>
        <span className={`${STATUS_BADGE} border-slate-300 bg-slate-100 text-slate-800`} data-testid="migration-status">{project.status?.replace(/_/g, " ")}</span>
      </div>

      <div className="border-b border-slate-200 mb-4 overflow-x-auto">
        <div className="flex items-center gap-1 min-w-max">
          {TABS.map((t) => (
            <button key={t.key} type="button" onClick={() => setTab(t.key)}
              className={`px-3 py-2 text-sm border-b-2 ${tab === t.key ? "border-emerald-600 text-emerald-700 font-semibold" : "border-transparent text-slate-500 hover:text-slate-800"}`}
              data-testid={`migration-tab-${t.key}`}
            >{t.label}</button>
          ))}
        </div>
      </div>

      {tab === "overview" && <OverviewTab project={project} refresh={refresh} />}
      {tab === "files"    && <UploadArea project={project} refresh={refresh} />}
      {tab === "mapping"  && <MappingTab project={project} refresh={refresh} />}
      {tab === "validation" && <ValidationTab project={project} refresh={refresh} />}
      {tab === "duplicates" && <DuplicatesTab project={project} />}
      {tab === "medication" && <MedicationTab project={project} />}
      {tab === "documents"  && <DocumentsTab project={project} />}
      {tab === "test"     && <TestTab project={project} refresh={refresh} />}
      {tab === "uat"      && <UatTab project={project} refresh={refresh} />}
      {tab === "approval" && <ApprovalTab project={project} refresh={refresh} />}
      {tab === "production" && <ProductionTab project={project} refresh={refresh} />}
      {tab === "reconciliation" && <ReconTab project={project} refresh={refresh} />}
      {tab === "reports"  && <ReportsTab project={project} />}
      {tab === "audit"    && <AuditTab project={project} />}
    </div>
  );
}
